import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { formatApiError } from '@/components/ProtectedRoute';

/**
 * One-click cleanup of test/dev data.
 * Triple-confirm to avoid accidents. Backend uses safe filters
 * (test email patterns, visitor_id v_test_*, before-date cutoff).
 */
export function CleanupTestDataButton() {
  const [opening, setOpening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [date, setDate] = useState('2026-04-23');

  const run = async () => {
    if (!window.confirm(
      "Es-tu sûr ? Va supprimer :\n\n" +
      `• Tout l'historique avant ${date}\n` +
      "• Les contacts de test (emails @cfsc.fr, [Exit-intent], [Formulaire express], etc.)\n" +
      "• Les events/pageviews tests (visitor_id v_test_*)\n\n" +
      "Les VRAIES demandes seront conservées."
    )) return;
    if (!window.confirm("Confirmation finale : on supprime ?")) return;

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/cleanup-test-data`, {
        before_date: date,
        delete_test_contacts: true,
        delete_test_events: true,
        delete_test_pageviews: true,
        delete_test_blog: false,
      });
      setResult(data);
      const total = (data.deleted?.contact_submissions || 0) + (data.deleted?.events || 0) + (data.deleted?.page_views || 0);
      toast.success(`Nettoyage terminé : ${total} entrées supprimées`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!opening) {
    return (
      <Card className="p-5 border-amber-500/30 bg-amber-50/30" data-testid="cleanup-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
            <Trash2 className="h-4 w-4 text-amber-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-base text-dark">Nettoyer les données de test</h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Supprime l'historique de la phase de développement et les éventuels événements de test.
              Les vraies demandes sont conservées.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpening(true)} data-testid="cleanup-open" className="rounded-lg shrink-0">
            Ouvrir
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 border-amber-500/30 bg-amber-50/30" data-testid="cleanup-card-open">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base text-dark">Nettoyer les données de test</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Supprime tout l'historique <strong>strictement avant</strong> la date choisie + les contacts/événements
            qui matchent des patterns de test (emails @cfsc.fr, exit-intent, etc).
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <label className="text-xs font-semibold text-dark">Garder uniquement après :</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="cleanup-date"
          className="text-xs border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={run}
          disabled={loading}
          data-testid="cleanup-run"
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Lancer le nettoyage
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { setOpening(false); setResult(null); }} className="rounded-lg">
          Annuler
        </Button>
      </div>

      {result && (
        <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-500/30 text-xs" data-testid="cleanup-result">
          <div className="font-semibold text-emerald-800 mb-1">✅ Nettoyage terminé</div>
          <div className="font-mono text-emerald-700/80">
            • Demandes contact supprimées : {result.deleted?.contact_submissions || 0}<br />
            • Événements supprimés : {result.deleted?.events || 0}<br />
            • Pageviews supprimées : {result.deleted?.page_views || 0}
          </div>
        </div>
      )}
    </Card>
  );
}
